import { Box, Button } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Currency } from '@nemuru-eng/js-api-client';
import { useEffect, useMemo, useRef } from 'react';

import { LenderRankingTable } from '@/components/modules/Metrics/LenderRanking/components/LenderRankingTable';
import { LenderRankingMap } from '@/components/modules/Metrics/LenderRanking/maps/lenderRanking.map';
import { EndDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/EndDateFilter';
import { MerchantIdFilter } from '@/components/modules/Metrics/shared/entities/Filter/MerchantIdFilter';
import { StartDateFilter } from '@/components/modules/Metrics/shared/entities/Filter/StartDateFilter';
import { QueryBuilder } from '@/components/modules/Metrics/shared/entities/QueryBuilder';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { useTimeIntervalLabel } from '@/components/modules/Metrics/shared/hooks/useTimeIntervalLabel';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { PendingDataType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useGetMetricLenderApplicationViewsByCriteria } from '@/hooks/requests/metric/useGetMetricLenderApplicationViewsByCriteria';
import { useGetMetricLenderViewsByCriteria } from '@/hooks/requests/metric/useGetMetricLenderViewsByCriteria';
import { useCustomTheme } from '@/hooks/useCustomTheme';

const currency = Currency.EUR;

interface LenderRankingProps {
  timeInterval: TimeInterval;
  merchantIds?: string[];
  metricKey?: ScoreCardMetricKey;
}

export const LenderRanking = ({
  timeInterval,
  merchantIds,
  metricKey = ScoreCardMetricKey.totalPrincipalAmount,
}: LenderRankingProps) => {
  const {
    state: { metricsRefreshToken },
  } = useOperationsContext();
  const { customBorderRadius } = useCustomTheme();
  const timeIntervalLabel = useTimeIntervalLabel(timeInterval);

  const query = useMemo(
    () =>
      new QueryBuilder([
        new StartDateFilter([timeInterval]),
        new EndDateFilter([timeInterval]),
        ...(merchantIds ? [new MerchantIdFilter(merchantIds)] : []),
      ]).createQuery(),
    [timeInterval, merchantIds],
  );

  const { metricLenderViews, metricLenderViewsError, revalidateMetricLenderViews } =
    useGetMetricLenderViewsByCriteria(query);
  const { metricLenderApplicationViews, metricLenderApplicationViewsError, revalidateMetricLenderApplicationViews } =
    useGetMetricLenderApplicationViewsByCriteria(query);

  const lastToken = useRef(metricsRefreshToken);
  useEffect(() => {
    if (lastToken.current === metricsRefreshToken) return;
    lastToken.current = metricsRefreshToken;
    revalidateMetricLenderViews();
    revalidateMetricLenderApplicationViews();
  }, [metricsRefreshToken]);

  const rows = useMemo(
    () =>
      metricLenderViews && metricLenderApplicationViews
        ? LenderRankingMap.toRows(metricLenderViews, metricLenderApplicationViews, timeInterval, metricKey)
        : undefined,
    [metricLenderViews, metricLenderApplicationViews, timeInterval, metricKey],
  );

  const error = metricLenderViewsError ?? metricLenderApplicationViewsError;

  return (
    <Box>
      <Box mb={3}>
        <Font variant="h4" color="title">
          <strong>
            <Text tId="LENDER_RANKING_TITLE" />
          </strong>
        </Font>
        <Font variant="body2" color="textSecondary">
          <Text tId="LENDER_RANKING_DESCRIPTION" dynamicDataTexts={[timeIntervalLabel.toLocaleLowerCase()]} />
        </Font>
      </Box>
      {error && (
        <CustomAlert
          severity="error"
          title="LENDER_RANKING_ERROR"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                revalidateMetricLenderViews();
                revalidateMetricLenderApplicationViews();
              }}
            >
              <Text tId="RETRY" />
            </Button>
          }
        />
      )}
      {!error && !rows && (
        <Skeleton variant="rect" width="100%" height={240} style={{ borderRadius: customBorderRadius.paper }} />
      )}
      {!error && rows && rows.lenders.length === 0 && (
        <PendingDataWarning type={PendingDataType.NO_METRICS_INFORMATION} />
      )}
      {!error && rows && rows.lenders.length > 0 && (
        <LenderRankingTable
          lenders={rows.lenders}
          summaries={rows.summaries}
          currency={currency}
          metricKey={metricKey}
        />
      )}
    </Box>
  );
};
